import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { BaseDialog } from '../../../../components/modals/BaseDialog'
import useBaseService from '../../../../hooks/services/useBaseService'
import useNotification from '../../../../hooks/useNotification'
import { entities } from '../../../../constants/entities'
import { useOrgan } from '../../../../hooks/useOrgan'
import { transformFiltersForUrl } from '../../../../utils/transformFiltersForUrl'
import { useNavigate } from 'react-router-dom'

const COMPANY_BY_ORGAN = {
  alucom: 'alucom',
  moreia: 'moreia',
  'as sistemas': 'as_sistemas',
  as_sistemas: 'as_sistemas',
  ip: 'ip',
}

const resolveTransportType = (movement) => {
  const modeId =
    movement?.order?.mode_transport_id ??
    movement?.order?.mode_transport?.id ??
    null
  if (modeId === 1) return 'rota'
  if (modeId === 2) return 'retirada'
  if (modeId === 3) return 'transportadora'

  const modeName = (
    movement?.order?.mode_transport?.name ||
    movement?.order?.modeTransport?.name ||
    ''
  ).toLowerCase()
  if (modeName.includes('retirad')) return 'retirada'
  if (modeName.includes('transport')) return 'transportadora'
  return 'rota'
}

const resolveCompany = (selectedOrgan) => {
  const orgName = (selectedOrgan?.name || selectedOrgan?.alias || '').toLowerCase()
  const matchedKey = Object.keys(COMPANY_BY_ORGAN).find((k) => orgName.includes(k))
  return matchedKey ? COMPANY_BY_ORGAN[matchedKey] : 'alucom'
}

const resolveCustomerName = (movement, customer = {}) =>
  customer.alias || customer.name || customer.customer_alias || movement.recipient_customer_name ||
  movement.customer_name || movement.recipient_name || ''

const resolveCustomerCnpj = (customer = {}, movement = {}) =>
  customer.cpf_cnpj || customer.cnpj || customer.cpf || customer.document || customer.tax_id || customer.document_number ||
  movement.recipient_customer_cnpj || movement.customer_cnpj || ''

const buildRomaneioItemsByVolume = (volumes, movementItemsById) =>
  volumes.map((volume, index) => {
    const volumeItems = volume.items || []
    const quantity = volumeItems.reduce((total, volumeItem) => {
      const movementItem = movementItemsById.get(
        String(volumeItem.movement_item_id),
      )
      const isEquipment =
        Boolean(movementItem?.equipment) ||
        String(volumeItem.type || '').toLowerCase().includes('equipment')
      return total + (isEquipment ? 1 : Number(volumeItem.quantity) || 0)
    }, 0)

    return {
      volume_id: volume.id ?? volume.volume_id ?? null,
      type:
        volume.name ||
        volume.description ||
        (volume.code ? `${volume.code} - ${volume.name || ''}` : '') ||
        `Volume ${index + 1}`,
      quantity,
      weight: Number(volume.weight ?? volume.total_weight) || 0,
    }
  })

const formatAddress = (movement, customer) => {
  const destination =
    movement?.order?.destination_order ||
    movement?.destination ||
    null

  if (typeof destination === 'string' && destination.trim()) return destination

  return (
    destination?.formatted_address ||
    destination?.address ||
    [
      destination?.street,
      destination?.number,
      destination?.neighborhood,
      destination?.city,
      destination?.state,
    ]
      .filter(Boolean)
      .join(', ') ||
    customer?.address ||
    ''
  )
}

const RomaneioModal = ({
  open,
  shipmentId,
  movementId,
  movementIds = [],
  receivedVolumes,
  pendingShipmentData,
  createShipmentMutation,
  onClose,
  onSuccess,
}) => {
  const navigate = useNavigate()
  const { get: baseServiceGet, post: baseServicePost } = useBaseService(navigate)
  const { showNotification } = useNotification()
  const { selectedOrgan } = useOrgan()

  const [formData, setFormData] = useState({
    customer_name: '',
    customer_cnpj: '',
    customer_address: '',
    type: 'rota',
    company: 'alucom',
    driver_id: '',
    driver_name: '',
    vehicle_number: '',
    items: [],
    volumes: 0,
    total_weight: 0,
  })
  const [groupForms, setGroupForms] = useState({})

  const [movementData, setMovementData] = useState(null)
  const [allMovementData, setAllMovementData] = useState([])
  const [drivers, setDrivers] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [createdShipmentId, setCreatedShipmentId] = useState(shipmentId || null)
  const groupedMovementData = useMemo(() => {
    const groups = new Map()
    const movements = allMovementData.length ? allMovementData : movementData ? [movementData] : []
    movements.forEach((movement) => {
      const customer = movement.recipient_customer?.customer || movement.recipient_customer || movement.customer?.customer || movement.customer || {}
      const address = formatAddress(movement, customer)
      const key = [movement.organization_id || '', movement.recipient_customer_id || customer.id || resolveCustomerName(movement, customer).trim().toLowerCase(), address.trim().toLowerCase().replace(/\s+/g, ' '), resolveTransportType(movement)].join('|')
      if (!groups.has(key)) groups.set(key, { key, movements: [], customer, address, type: resolveTransportType(movement) })
      groups.get(key).movements.push(movement)
    })
    return [...groups.values()]
  }, [allMovementData, movementData])

  useEffect(() => {
    if (!groupedMovementData.length) return
    setGroupForms((current) => {
      const next = { ...current }
      groupedMovementData.forEach((group) => {
        if (next[group.key]) {
          next[group.key] = {
            ...next[group.key],
            customer_name: next[group.key].customer_name || resolveCustomerName(group.movements[0], group.customer),
            customer_cnpj: next[group.key].customer_cnpj || resolveCustomerCnpj(group.customer, group.movements[0]),
          }
          return
        }
        const ids = new Set(group.movements.map((movement) => String(movement.id)))
        const movementItems = new Map(group.movements.flatMap((movement) => (movement.items || []).map((item) => [String(item.id), item])))
        const groupVolumes = (receivedVolumes || []).filter((volume) => ids.has(String(volume.movement_id)))
        const items = buildRomaneioItemsByVolume(groupVolumes, movementItems)
        next[group.key] = { customer_name: resolveCustomerName(group.movements[0], group.customer), customer_cnpj: resolveCustomerCnpj(group.customer, group.movements[0]), customer_address: group.address, items, total_weight: Number(items.reduce((sum, item) => sum + item.weight, 0).toFixed(2)), volumes: groupVolumes.length, driver_id: '', vehicle_number: '' }
      })
      return next
    })
  }, [groupedMovementData, receivedVolumes])
  const baseServiceGetRef = useRef(baseServiceGet)
  useEffect(() => {
    baseServiceGetRef.current = baseServiceGet
  }, [baseServiceGet])

  useEffect(() => {
    const ids = movementIds.length ? movementIds : movementId ? [movementId] : []
    if (!open || !ids.length) return

    let cancelled = false
    setIsLoading(true)

    Promise.all(ids.map((id) => baseServiceGetRef.current(entities.movements.getById(id))))
      .then((responses) => {
        if (!cancelled) {
          const movements = responses.map((res) => res?.result).filter(Boolean)
          setAllMovementData(movements)
          setMovementData(movements[0] || null)
        }
      })
      .catch(() => {
        if (!cancelled) setMovementData(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, movementId, movementIds])

  useEffect(() => {
    setCreatedShipmentId(shipmentId || null)
  }, [shipmentId])

  // ✅ COLAR AQUI: Carrega motoristas (1x por abertura)
  useEffect(() => {
    if (!open) return

    let cancelled = false
    setIsLoadingDrivers(true)

    const filters = transformFiltersForUrl({
      'roles.name': 'Motorista',
      deleted_at: false,
    })

    baseServiceGetRef.current(entities.users.get, {
      filters,
      perPage: 100,
      page: 1,
    })
      .then((res) => {
        if (!cancelled) setDrivers(res?.result?.data || [])
      })
      .catch(() => {
        if (!cancelled) setDrivers([])
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDrivers(false)
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (!open || !movementData) return

    const customer = movementData.recipient_customer || movementData.customer || {}
    const movementItemsById = new Map(
      (movementData.items || []).map((item) => [String(item.id), item])
    )

    const volumesList = receivedVolumes || []
    const itemsFromVolumes = buildRomaneioItemsByVolume(
      volumesList,
      movementItemsById,
    )

    const items =
      itemsFromVolumes.length > 0
        ? itemsFromVolumes
        : movementData.items?.length
          ? [{
              type: 'Volume sem identificação',
              quantity: movementData.items.reduce(
                (total, item) => total + (Number(item.quantity) || 1),
                0,
              ),
              weight: 0,
            }]
          : []

    const totalWeight = items.reduce(
      (acc, item) => acc + (Number(item.weight) || 0),
      0
    )

    setFormData({
      customer_name: customer.alias || customer.name || movementData.recipient_customer_name || '',
      customer_cnpj: customer.cpf_cnpj || customer.cnpj || '',
      customer_address: formatAddress(movementData, customer),
      type: resolveTransportType(movementData),
      company: resolveCompany(selectedOrgan),
      driver_id: '',
      driver_name: '',
      vehicle_number: '',
      items,
      volumes: volumesList.length || items.length,
      total_weight: Number(totalWeight.toFixed(2)),
    })
  }, [open, movementData, receivedVolumes, selectedOrgan])

  useEffect(() => {
    if (!open || !allMovementData.length) return
    let cancelled = false
    const unresolved = allMovementData.filter((movement) => {
      const customer = movement.recipient_customer?.customer || movement.recipient_customer || movement.customer?.customer || movement.customer
      const customerId = movement.recipient_customer_id || customer?.id
      return !movement._customerResolved && !customer?.alias && !customer?.name && Boolean(customerId)
    })
    if (!unresolved.length) return

    const customerIds = [...new Set(unresolved.map((movement) => String(
      movement.recipient_customer_id || movement.recipient_customer?.customer?.id || movement.recipient_customer?.id || movement.customer?.customer?.id || movement.customer?.id
    )))]
    Promise.all(customerIds.map(async (customerId) => {
      try {
        const response = await baseServiceGetRef.current(entities.customers.getById(customerId))
        return [customerId, response?.result || response?.data?.result || response?.data || {}]
      } catch (_) { return [customerId, null] }
    })).then((customerResults) => {
      if (cancelled) return
      const customersById = new Map(customerResults)
      const resolvedMovements = unresolved.map((movement) => {
        const customer = movement.recipient_customer?.customer || movement.recipient_customer || movement.customer?.customer || movement.customer
        const customerId = String(movement.recipient_customer_id || customer?.id)
        const foundCustomer = customersById.get(customerId)
        return { ...movement, _customerResolved: true, ...(foundCustomer ? { recipient_customer: foundCustomer } : {}) }
      })
      const resolvedById = new Map(resolvedMovements.map((movement) => [String(movement.id), movement]))
      const nextMovements = allMovementData.map((movement) => resolvedById.get(String(movement.id)) || movement)
      const changed = nextMovements.some((movement, index) => movement !== allMovementData[index])
      if (!changed) return
      setAllMovementData(nextMovements)
      setMovementData((current) => {
        const updatedFirst = nextMovements[0] || null
        return current?.id === updatedFirst?.id ? updatedFirst : current
      })
    })
    return () => { cancelled = true }
  }, [open, allMovementData])

  const handleSubmit = useCallback(async () => {
    if (!createdShipmentId && (!pendingShipmentData || !createShipmentMutation)) {
      showNotification('error', 'ID do carregamento não encontrado.')
      return
    }
    if (!groupedMovementData.length || groupedMovementData.some((group) => !groupForms[group.key]?.customer_name?.trim())) {
      showNotification('warning', 'Preencha o nome do cliente.')
      return
    }

    setIsSubmitting(true)
    try {
      let targetShipmentId = createdShipmentId || shipmentId
      if (!targetShipmentId) {
        const created = await createShipmentMutation.mutateAsync(pendingShipmentData)
        targetShipmentId = created?.result?.id ?? created?.result?.shipment?.id ?? created?.result?.data?.id ?? created?.data?.result?.id ?? created?.data?.id ?? created?.id ?? null
        if (!targetShipmentId) throw new Error('ID do carregamento não encontrado após a criação.')
        setCreatedShipmentId(targetShipmentId)
      }
      const responses = []
      for (const group of groupedMovementData) {
      const movements = group.movements
      const groupForm = groupForms[group.key]
      if (!groupForm) throw new Error('Os dados do grupo ainda estão carregando.')
      const representative = movements[0]
      const payload = {
        movement_id: representative.id || movementId || null,
        movement_ids: movements.map((movement) => movement.id),
        recipient_customer_id: representative.recipient_customer_id || null,
        customer_name: groupForm.customer_name,
        customer_cnpj: groupForm.customer_cnpj,
        customer_address: groupForm.customer_address,
        type: group.type,
        company: formData.company,
        driver_id: groupForm.driver_id || null,
        driver_name: drivers.find((driver) => String(driver.id) === String(groupForm.driver_id))?.name || null,
        vehicle_number: groupForm.vehicle_number || null,
        items: groupForm.items,
        volumes: groupForm.volumes,
        total_weight: groupForm.total_weight,
      }

      const response = await baseServicePost(
        entities.shipments.romaneio.create(targetShipmentId),
        payload,
        { skipNotification: true }
      )
      responses.push(response)
      }
      const response = responses[0]
      if (
        response?.status === 'created' ||
        response?.status === 'success' ||
        response?.status === 201 ||
        response?.status === 200
      ) {
        showNotification('success', 'Romaneio criado com sucesso.')
        onSuccess?.(targetShipmentId)
      }
    } catch (error) {
      showNotification(
        'error',
        error?.data?.message || error?.message || 'Erro ao criar romaneio.'
      )
      console.error('Error creating romaneio:', error)
    } finally {
      setIsSubmitting(false)
    }
  }, [
    shipmentId,
    createdShipmentId,
    pendingShipmentData,
    createShipmentMutation,
    movementId,
    formData,
    baseServicePost,
    showNotification,
    onSuccess,
    drivers,
    groupedMovementData,
    groupForms,
  ])

  const actions = (
    <>
      <Button onClick={onClose} color="secondary" disabled={isSubmitting}>
        Cancelar
      </Button>
      <Button
        onClick={handleSubmit}
        variant="contained"
        disabled={isSubmitting || isLoading || (!shipmentId && !createdShipmentId && (!pendingShipmentData || !createShipmentMutation))}
        startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
      >
        {isSubmitting ? 'Salvando...' : 'Salvar Romaneio'}
      </Button>
    </>
  )

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="Romaneio - Informações para Nota Fiscal"
      maxWidth="md"
      actions={actions}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {groupedMovementData.map((group) => {
            const values = groupForms[group.key]
            if (!values) return null
            const setField = (field, value) => setGroupForms((current) => ({ ...current, [group.key]: { ...current[group.key], [field]: value } }))
            const setWeight = (index, value) => setGroupForms((current) => {
              const updated = { ...current[group.key], items: [...current[group.key].items] }
              updated.items[index] = { ...updated.items[index], weight: Number(value) || 0 }
              updated.total_weight = Number(updated.items.reduce((sum, item) => sum + (Number(item.weight) || 0), 0).toFixed(2))
              return { ...current, [group.key]: updated }
            })
            return <Paper key={group.key} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>{group.customer.alias || group.customer.name || 'Cliente'} · {group.type}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}><TextField label="Cliente" value={values.customer_name} onChange={(event) => setField('customer_name', event.target.value)} fullWidth size="small" /></Grid>
                <Grid item xs={12} md={6}><TextField label="CNPJ" value={values.customer_cnpj} onChange={(event) => setField('customer_cnpj', event.target.value)} fullWidth size="small" /></Grid>
                <Grid item xs={12}><TextField label="Endereço de destino" value={values.customer_address} onChange={(event) => setField('customer_address', event.target.value)} fullWidth multiline rows={2} size="small" /></Grid>
                <Grid item xs={12} md={4}><TextField label="Transporte" value={group.type} fullWidth size="small" InputProps={{ readOnly: true }} /></Grid>
                <Grid item xs={12} md={4}><TextField label="Empresa" value={resolveCompany(selectedOrgan)} fullWidth size="small" InputProps={{ readOnly: true }} /></Grid>
                <Grid item xs={12} md={4}><TextField select label="Motorista" value={values.driver_id} onChange={(event) => setField('driver_id', event.target.value)} fullWidth size="small" disabled={isLoadingDrivers}>{drivers.map((driver) => <MenuItem key={driver.id} value={driver.id}>{driver.name}</MenuItem>)}</TextField></Grid>
                <Grid item xs={12}><TextField label="Veículo" value={values.vehicle_number} onChange={(event) => setField('vehicle_number', event.target.value)} fullWidth size="small" placeholder="Ex: ABC-1234" /></Grid>
              </Grid>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Itens</Typography>
              <TableContainer component={Paper} variant="outlined"><Table size="small"><TableHead><TableRow><TableCell>Quantidade de itens</TableCell><TableCell>Peso do volume (kg)</TableCell><TableCell>Volume</TableCell></TableRow></TableHead><TableBody>{values.items.map((item, index) => <TableRow key={`${item.volume_id ?? item.type}-${index}`}><TableCell>{item.quantity}</TableCell><TableCell><TextField type="number" value={item.weight ?? 0} onChange={(event) => setWeight(index, event.target.value)} size="small" inputProps={{ min: 0, step: 0.01 }} /></TableCell><TableCell>{item.type}</TableCell></TableRow>)}</TableBody></Table></TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography color="text.secondary">Volumes do grupo: {values.volumes}</Typography>
                <TextField label="Peso total (kg)" type="number" value={values.total_weight} onChange={(event) => setField('total_weight', Number(event.target.value) || 0)} size="small" inputProps={{ min: 0, step: 0.01 }} />
              </Box>
            </Paper>
          })}
        </Box>
      )}
    </BaseDialog>
  )
}

export default RomaneioModal
